/**
 * QR Scanner Test Page
 * 
 * Simple test page to verify the QR scanner functionality
 * Navigate to /test/qr-scanner to test
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  Divider,
} from '@mui/material';
import { QrCodeScanner, CheckCircle } from '@mui/icons-material';
import { QRCodeScanner } from '@healthdesk/shared-components';

const QRScannerTestPage = () => {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannedData, setScannedData] = useState(null);
  const [error, setError] = useState(null);

  const handleScanSuccess = async (data) => {
    setScannedData(data);
    setError(null);
  };

  const handleScanError = (err) => {
    console.error('[QR Scanner Test] Error:', err);
    setError(err.message || 'Scan error');
  };

  const handleReset = () => {
    setScannedData(null);
    setError(null);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <QrCodeScanner color="primary" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            QR Scanner Test Page
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Test the standalone QR scanner component
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Scan Button */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<QrCodeScanner />}
            onClick={() => setScannerOpen(true)}
          >
            Open QR Scanner
          </Button>
        </Box>

        {/* Results */}
        {scannedData && (
          <Alert 
            severity="success" 
            icon={<CheckCircle />}
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={handleReset}>
                Reset
              </Button>
            }
          >
            <Typography variant="subtitle2" gutterBottom>
              Successfully Scanned!
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {scannedData}
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert 
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {/* Instructions */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom>
            Instructions:
          </Typography>
          <Typography variant="body2" component="div">
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>Click "Open QR Scanner" button</li>
              <li>Allow camera permissions if prompted</li>
              <li>Point your camera at a QR code</li>
              <li>The scanner will automatically detect and decode it</li>
              <li>For testing without a physical QR code, use the "Manual Entry" tab</li>
            </ol>
          </Typography>
        </Paper>

        {/* Dev Mode Info */}
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="caption">
            <strong>Dev Mode:</strong> You can also test with manual entry. 
            Try entering any test string in the "Manual Entry" tab.
          </Typography>
        </Alert>
      </Paper>

      {/* QR Scanner Dialog */}
      <QRCodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScanSuccess={handleScanSuccess}
        onScanError={handleScanError}
        title="Test QR Scanner"
        description="Scan any QR code to test the scanner"
        allowManualEntry={true}
        manualEntryLabel="Test Code"
      />
    </Container>
  );
};

export default QRScannerTestPage;

